/**
 * A meeting on the month calendar (TMMAPP-0103, folded by TMMAPP-0108): one chip for My Meetings'
 * Calendar tab and the virtual account's, so the two never drift. Its label is the entry; its paint is
 * the occurrence's status tone, and a live meeting is the static `brand` / `brandInk` pill (visual
 * contract §9; TMM-T4: no pulse). The live fill is laid out like `toneChipSx` — rest, hover and
 * keyboard focus — so it beats MUI's filled chip as plain `sx`, with no specificity override.
 */
import { Chip, useTheme } from '@mui/material';
import type { MeetingCalendarEntry } from '@/api/meetings';
import { toneFor } from '@/theme/statusTones';
import { resolveTokens } from '@/theme/theme';
import { toneChipSx } from '@/theme/toneChipSx';
import { entryStatus } from './calendarEntry';

interface CalendarEntryChipProps {
  entry: MeetingCalendarEntry;
  label: string;
  onClick: (anchor: HTMLElement) => void;
}

export function CalendarEntryChip({ entry, label, onClick }: CalendarEntryChipProps) {
  const tokens = resolveTokens(useTheme());
  const live = {
    backgroundColor: tokens.brand,
    backgroundImage: 'none',
    color: tokens.brandInk,
    '& .MuiChip-label, & .MuiChip-icon': { color: tokens.brandInk },
  };
  const paint = entry.live
    ? {
        ...live,
        '&.MuiChip-clickable:hover': {
          ...live,
          boxShadow: `inset 0 0 0 1px ${tokens.lineStrong}`,
        },
        '&.Mui-focusVisible': {
          ...live,
          outline: `2px solid ${tokens.focusRing}`,
          outlineOffset: 2,
        },
      }
    : toneChipSx(tokens, toneFor('meeting', entryStatus(entry)));
  return (
    <Chip
      size="small"
      label={label}
      onClick={(event) => onClick(event.currentTarget)}
      data-live={entry.live ? '' : undefined}
      sx={{ ...paint, maxWidth: '100%' }}
    />
  );
}
